import { useEffect, useRef, useState } from "react";
import { motion, useInView, Variants } from "framer-motion";

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "left" | "right" | "none";
  /** Enable staggered children animation — each direct child animates in sequence */
  stagger?: boolean;
  /** Delay between each staggered child (seconds) */
  staggerDelay?: number;
}

const containerVariants: Variants = {
  hidden: {},
  visible: (staggerDelay: number) => ({
    transition: {
      staggerChildren: staggerDelay,
      delayChildren: 0.1,
    },
  }),
};

const childVariants = (direction: "up" | "left" | "right" | "none"): Variants => {
  const directionMap = {
    up: { y: 16, x: 0 },
    left: { y: 0, x: -20 },
    right: { y: 0, x: 20 },
    none: { y: 0, x: 0 },
  };
  const { x, y } = directionMap[direction];

  return {
    hidden: { opacity: 0, y, x },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
    },
  };
};

export const ScrollReveal = ({
  children,
  className,
  delay = 0,
  direction = "up",
  stagger = false,
  staggerDelay = 0.08,
}: ScrollRevealProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const [fallback, setFallback] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFallback(true), 800);
    return () => clearTimeout(t);
  }, []);
  const show = isInView || fallback;

  if (stagger) {
    return (
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={show ? "visible" : "hidden"}
        custom={staggerDelay}
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  const directionMap = {
    up: { y: 16, x: 0 },
    left: { y: 0, x: -20 },
    right: { y: 0, x: 20 },
    none: { y: 0, x: 0 },
  };
  const { x, y } = directionMap[direction];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y, x }}
      animate={show ? { opacity: 1, y: 0, x: 0 } : { opacity: 0, y, x }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/** Wrap each stagger child in this for automatic animation */
export const ScrollRevealItem = ({
  children,
  className,
  direction = "up",
}: {
  children: React.ReactNode;
  className?: string;
  direction?: "up" | "left" | "right" | "none";
}) => (
  <motion.div variants={childVariants(direction)} className={className}>
    {children}
  </motion.div>
);
